import Dialog from '@/components/_custom/Dialog/Dialog.tsx'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader.tsx'
import DialogBody from '@/components/_custom/DialogBody/DialogBody.tsx'
import { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UPDATE_DAYCARE_MEAL_ACCESSIBILITY } from '@/modules/API/meal/daycareSettingsHandler.ts'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { useMutation } from '@apollo/client'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import styles from '../../meal.module.scss'
import { Daycare } from '@/types/Daycare.ts'
import { MealAccessibilitySection, MealType } from '@/types/Meal.ts'
import moment from 'moment-timezone'
import TimePicker from '@/components/_custom/Picker/timePicker/timePicker.tsx'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique.tsx'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import { Section } from '@/types/Section.ts'

interface UpdateMealAccessibilityDialogProps {
    open: boolean
    handleClose: () => void
    refetch: () => void
    daycare: Daycare
    accessibilitySection: MealAccessibilitySection
    isRPEOrLAEP: boolean
}

const UpdateMealAccessibilityDialog: FunctionComponent<UpdateMealAccessibilityDialogProps> = ({
    open,
    handleClose,
    refetch,
    daycare,
    accessibilitySection,
    isRPEOrLAEP
}: UpdateMealAccessibilityDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [sectionId, setSectionId] = useState<string>(accessibilitySection.sectionId.toString())
    const [start, setStart] = useState<string>(moment(accessibilitySection.start, 'HH:mm:ss').format('HH:mm'))
    const [type, setType] = useState<string>(accessibilitySection.type)

    const [updateMealAccessibility] = useMutation(UPDATE_DAYCARE_MEAL_ACCESSIBILITY, {
        variables: {
            daycareId: daycare.id,
            sectionId: Number(sectionId),
            start,
            accessibilitySectionId: accessibilitySection.id,
            type
        },
        onCompleted: () => {
            refetch()
            handleClose()
            setSectionId('')
            setStart('')
            setType('')
            showNotificationSuccess(t('mealAccessibilityUpdated'))
        },
        onError: () => {
            handleClose()
            setSectionId('')
            setStart('')
            setType('')
            showNotificationError(t('mealAccessibilityUpdatedError'))
        }
    })

    const mealTypeOptions: SelectOptionsDeprecated = [
        {
            options: Object.values(MealType).map(
                (mealType: MealType): SelectOptionDeprecated => ({
                    name: t('mealType' + mealType.charAt(0).toUpperCase() + mealType.slice(1)),
                    value: mealType
                })
            )
        }
    ]

    const sectionOptions: SelectOptionsDeprecated = [
        {
            options:
                daycare.sections?.map((section: Section): SelectOptionDeprecated => {
                    return {
                        name: section.name,
                        value: section.id.toString()
                    }
                }) || []
        }
    ]

    useEffect(() => {
        setSectionId(accessibilitySection.sectionId.toString())
        setStart(moment(accessibilitySection.start, 'HH:mm:ss').format('HH:mm'))
        setType(accessibilitySection.type)
    }, [accessibilitySection])

    const disabled: boolean =
        sectionId === '0' ||
        start === '' ||
        type === '' ||
        (sectionId === accessibilitySection.sectionId.toString() &&
            start === moment(accessibilitySection.start, 'HH:mm:ss').format('HH:mm') &&
            type === accessibilitySection.type)

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('updateMealAccessibility')} />
            <DialogBody className={styles.DialogBody}>
                <SelectUnique
                    label={isRPEOrLAEP ? t('mySectors') : t('mySections')}
                    selectOptions={sectionOptions}
                    selectValue={sectionId}
                    setSelectValue={setSectionId}
                    className={styles.UserInput}
                    fullWidth={true}
                />
                <TimePicker
                    label={t('hour')}
                    value={start}
                    setValue={setStart}
                    minuteToDisable={5}
                    className={styles.UserInput}
                />
                <SelectUnique
                    label={t('mealType')}
                    selectOptions={mealTypeOptions}
                    selectValue={type}
                    setSelectValue={setType}
                    className={styles.UserInput}
                    fullWidth={true}
                />
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type='close' onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type='update'
                        disabled={disabled}
                        onClick={() => updateMealAccessibility()}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default UpdateMealAccessibilityDialog
